import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, LogOut, Lock, Clock, Users, HelpCircle, ChevronDown, DatabaseBackup, Loader2 } from 'lucide-react';
import { downloadBackupWorkbook, cycleLabelOf } from '../utils/backup';

const APP_VERSION = '2.0.15';

const UserPanel = ({ isOpen, onClose, user, onLogout, onOpenChangeLogs, onOpenUserManagement, onResetPassword }) => {
  const [showGuide, setShowGuide] = React.useState(false);
  // Manual half of the backup. The automatic one runs itself once per 4-month
  // cycle at login (see utils/backup.js); this is the "I want it now" button.
  const [backupState, setBackupState] = React.useState({ busy: false, message: '' });

  const handleDownloadBackup = async () => {
    setBackupState({ busy: true, message: '' });
    try {
      const { counts, failures } = await downloadBackupWorkbook({ trigger: 'manual' });
      const total = Object.values(counts).reduce((a, b) => a + b, 0);
      setBackupState({
        busy: false,
        message: failures.length
          ? `Downloaded ${total} records — ${failures.map(f => f.sheet).join(', ')} could not be fetched`
          : `Downloaded ${total} records across ${Object.keys(counts).length} sheets`
      });
    } catch (e) {
      setBackupState({ busy: false, message: `Backup failed: ${e.message}` });
    }
  };

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const userRole = user?.role === 'admin' ? 'ADMINISTRATOR' : (user?.role || 'BDE').toUpperCase();

  const panelContent = (
    <>
      <div
        className="fixed inset-0 bg-black/40 z-[9998] transition-opacity"
        onClick={onClose}
        role="button"
        tabIndex={-1}
        aria-label="Close panel"
      />
      <div
        className="fixed top-0 right-0 h-full w-full max-w-sm bg-white shadow-2xl z-[9999] flex flex-col"
        style={{
          animation: 'userPanelSlideIn 0.25s ease-out',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.15)'
        }}
      >
        <style>{`
          @keyframes userPanelSlideIn {
            from { transform: translateX(100%); }
            to { transform: translateX(0); }
          }
        `}</style>

        {/* Close button */}
        <div className="flex items-center justify-end p-4">
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Close panel"
          >
            <X size={22} />
          </button>
        </div>

        {/* User identity */}
        <div className="px-6 pb-6 flex flex-col items-center">
          <div className="w-28 h-28 rounded-full flex items-center justify-center bg-gray-50 overflow-hidden flex-shrink-0">
            <img
              src="/leftpanel.png"
              alt=""
              className="w-24 h-24 object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
                const next = e.target.nextElementSibling;
                if (next) next.classList.remove('hidden');
              }}
            />
            <div className="hidden w-24 h-24 rounded-full bg-[#86bb46]/20 flex items-center justify-center text-[#679632] font-bold text-3xl">
              {user?.name?.charAt(0) || 'U'}
            </div>
          </div>
          <h2 className="mt-4 text-xl font-bold text-gray-900">{user?.name || 'User'}</h2>
          <p className="mt-1 text-sm text-gray-500">{user?.email || user?.username || ''}</p>
          <span className="mt-2 px-4 py-1 text-xs font-semibold tracking-wider text-[#679632] border border-[#679632]/40 rounded-full bg-[#679632]/5">
            {userRole}
          </span>
        </div>

        {/* Divider */}
        <div className="mx-6 border-t border-gray-100" />

        {/* Account actions */}
        <div className="flex-1 px-6 py-4 space-y-1">
          {user?.role === 'admin' && onOpenUserManagement && (
            <button
              type="button"
              onClick={() => { onOpenUserManagement(); onClose(); }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-gray-700 hover:bg-gray-50 border border-gray-100 transition-colors text-sm text-left"
            >
              <Users size={18} className="text-gray-400 flex-shrink-0" />
              Manage Users
            </button>
          )}
          {user?.role === 'admin' && (
            <>
              <button
                type="button"
                onClick={handleDownloadBackup}
                disabled={backupState.busy}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-gray-700 hover:bg-gray-50 border border-gray-100 transition-colors text-sm text-left disabled:opacity-60"
                title="Download every record as one Excel workbook"
              >
                {backupState.busy
                  ? <Loader2 size={18} className="text-gray-400 flex-shrink-0 animate-spin" />
                  : <DatabaseBackup size={18} className="text-gray-400 flex-shrink-0" />}
                <span>{backupState.busy ? 'Building backup…' : 'Download Backup (Excel)'}</span>
                <span className="ml-auto text-xs text-gray-400">{cycleLabelOf()}</span>
              </button>
              {backupState.message && (
                <p className="px-4 pb-1 text-xs text-gray-500">{backupState.message}</p>
              )}
            </>
          )}
          <button
            type="button"
            onClick={() => { if (onResetPassword) onResetPassword(); onClose(); }}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-gray-700 hover:bg-gray-50 border border-gray-100 transition-colors text-sm text-left"
          >
            <Lock size={18} className="text-gray-400 flex-shrink-0" />
            Reset Password
          </button>
          <button
            type="button"
            onClick={() => { if (onOpenChangeLogs) onOpenChangeLogs(); onClose(); }}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-gray-700 hover:bg-gray-50 border border-gray-100 transition-colors text-sm text-left"
          >
            <Clock size={18} className="text-gray-400 flex-shrink-0" />
            <span>Change Logs</span>
            <span className="ml-auto text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-medium">v{APP_VERSION}</span>
          </button>

          {/* App Guide */}
          <button
            type="button"
            onClick={() => setShowGuide(!showGuide)}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-gray-700 hover:bg-gray-50 border border-gray-100 transition-colors text-sm text-left"
          >
            <HelpCircle size={18} className="text-gray-400 flex-shrink-0" />
            <span>App Guide</span>
            <ChevronDown size={14} className={`ml-auto text-gray-400 transition-transform ${showGuide ? 'rotate-180' : ''}`} />
          </button>
          {showGuide && (
            <div className="mx-1 p-4 bg-gray-50 rounded-xl border border-gray-100 text-xs text-gray-700 space-y-3 max-h-60 overflow-y-auto">
              <p className="font-semibold text-gray-900 text-sm">AGS Sales Hub</p>
              <p>A complete sales CRM for managing leads, follow-ups, clients, and team performance across the entire sales lifecycle.</p>
              <div>
                <p className="font-semibold text-gray-800 mb-1">Pages</p>
                <ul className="space-y-1 text-gray-600">
                  <li><span className="font-medium text-gray-800">Pre Sales</span> — Add and manage leads. Track POCs, emails, connections, and follow-ups.</li>
                  <li><span className="font-medium text-gray-800">Post Sales</span> — Manage companies after initial contact. Track call status, follow-ups, and notes.</li>
                  <li><span className="font-medium text-gray-800">Potential Clients</span> — Companies moved here from Pre/Post Sales when they show interest. Close deals to move to Onboarded.</li>
                  <li><span className="font-medium text-gray-800">Onboarded Clients</span> — Closed deals. Upload documents, track onboarding status.</li>
                  <li><span className="font-medium text-gray-800">Follow-ups</span> — All pending follow-ups across Pre and Post Sales in one view. Filter by date, BDE, status.</li>
                  <li><span className="font-medium text-gray-800">Send Emails</span> — Compose and send emails to leads. Use templates, attach files, send in bulk.</li>
                  <li><span className="font-medium text-gray-800">Templates</span> — Create and edit email templates by stage (Initial Pitch, Follow-ups, etc.).</li>
                  <li><span className="font-medium text-gray-800">Analytics</span> — KPIs, pipeline charts, BDE performance comparisons.</li>
                  <li><span className="font-medium text-gray-800">Activity</span> — Daily activity log. Today vs yesterday comparison.</li>
                  <li><span className="font-medium text-gray-800">Highlights</span> — Daily accomplishments, pending tasks, and notes per BDE.</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold text-gray-800 mb-1">Tips</p>
                <ul className="space-y-1 text-gray-600">
                  <li>Click any company name to open its Pre Sales form.</li>
                  <li>Use Ctrl+S / Cmd+S to quick-save in templates and forms.</li>
                  <li>The green sparkle button opens the AI Virtual Sales Agent.</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Logout */}
        <div className="p-6 border-t border-gray-100">
          <button
            type="button"
            onClick={() => { onLogout(); onClose(); }}
            className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl text-white font-medium transition-colors bg-red-600 hover:bg-red-700"
          >
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </div>
    </>
  );

  return createPortal(panelContent, document.body);
};

export default UserPanel;
